import { useEffect, useRef, useState } from "react";
import { Kv, CopyBtn } from "../vault/Common";
import {
  getStorage,
  getStorageUsage,
  connectStorage,
  disconnectStorage,
  type StorageStatus,
  type StorageUsage,
  type ProvisionStep,
} from "../../features/vault/vaultApi";
import { listAgents, createAgent, deleteAgent, type Agent } from "../../features/render/renderApi";

const REGIONS = ["us-east-1", "us-west-2", "eu-west-1", "eu-central-1", "ap-south-1", "ap-southeast-1", "ap-southeast-2"];
const STEP_ORDER: ProvisionStep[] = [
  "validating", "creating-bucket", "securing-bucket", "configuring-cors", "creating-cdn", "finalizing",
];

function gb(bytes: number): string {
  if (!bytes || bytes < 0) return "0 GB";
  const g = bytes / 1024 ** 3;
  if (g >= 1) return `${g.toFixed(1)} GB`;
  const m = bytes / 1024 ** 2;
  if (m >= 1) return `${m.toFixed(0)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * Bring-your-own AWS storage. Reuses the proven getStorage/connectStorage/
 * disconnectStorage flow from StorageSettings.tsx, rendered in the reference
 * "Storage & S3" DOM. Provider dropdown, manual bucket field, Save/Test/Sync
 * buttons and the Behaviour toggles are gone — the backend auto-creates the
 * bucket + CDN and has no endpoint behind those controls.
 */
export default function StoragePage() {
  const [status, setStatus] = useState<StorageStatus | null>(null);
  const [usage, setUsage] = useState<StorageUsage | null>(null);
  const [keyId, setKeyId] = useState("");
  const [secret, setSecret] = useState("");
  const [region, setRegion] = useState("us-east-1");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const provisioning = status?.provisioning;
  const isProvisioning = Boolean(provisioning && provisioning.step !== "ready" && provisioning.step !== "error");

  const refreshUsage = () => getStorageUsage().then(setUsage).catch(() => undefined);

  useEffect(() => {
    getStorage().then(setStatus).catch(() => setStatus({ enabled: false, region: null, bucket: null, cloudfrontDomain: null, accessKeyId: null, provisioning: null }));
    refreshUsage();
  }, []);

  // Poll live provisioning progress ~2.5s while a provision is running.
  useEffect(() => {
    if (!isProvisioning) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    pollRef.current = setInterval(() => {
      getStorage().then(setStatus).catch(() => undefined);
    }, 2500);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isProvisioning]);

  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      const s = await connectStorage(keyId.trim(), secret.trim(), region);
      setStatus(s);
      setKeyId("");
      setSecret("");
      refreshUsage();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not connect");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect your storage and use Jewelitt storage again? Existing models stay in your bucket.")) return;
    setBusy(true);
    setError(null);
    try {
      await disconnectStorage();
      setStatus({ enabled: false, region: null, bucket: null, cloudfrontDomain: null, accessKeyId: null, provisioning: null });
      refreshUsage();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not disconnect");
    } finally {
      setBusy(false);
    }
  };

  const loading = status === null;
  const connected = Boolean(status?.enabled) && !isProvisioning;
  const dotStyle = connected ? undefined : { background: "var(--muted)", boxShadow: "none" as const };
  const stepIdx = provisioning ? STEP_ORDER.indexOf(provisioning.step) : -1;

  const used = usage?.usedBytes ?? 0;
  const total = usage?.totalBytes ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;

  return (
    <>
      <div className="page-title">Storage &amp; S3</div>
      <div className="page-sub">Keep your masters in your own bucket. Vault reads, previews and serves — you keep ownership.</div>

      {/* ───── Connection banner (loading / provisioning / connected / not-connected) ───── */}
      <div className="card" style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 14 }}>
        <span className="conn-dot" style={dotStyle}></span>
        <div style={{ flex: 1 }}>
          {loading ? (
            <b style={{ fontSize: 14 }}>Checking your storage…</b>
          ) : isProvisioning ? (
            <>
              <b style={{ fontSize: 14 }}>Setting up your storage…</b>
              <div className="hint">
                {stepIdx >= 0 ? `Step ${stepIdx + 1} of ${STEP_ORDER.length} · ` : ""}
                {provisioning?.message}
              </div>
            </>
          ) : connected ? (
            <>
              <b style={{ fontSize: 14 }}>Connected — <span className="code">s3://{status?.bucket}</span></b>
              <div className="hint">
                Amazon S3 · {status?.region}
                {status?.cloudfrontDomain ? <> · CDN <span className="code">{status.cloudfrontDomain}</span></> : null}
                {usage ? ` · ${usage.modelCount} models mirrored` : ""}
                {provisioning?.cdnStatus === "deploying" ? " · CDN still deploying (~15 min)" : ""}
              </div>
              {error && <div className="hint" style={{ color: "var(--red)" }}>{error}</div>}
            </>
          ) : (
            <>
              <b style={{ fontSize: 14 }}>Using Jewelitt storage</b>
              <div className="hint">Connect your own AWS account below to keep ownership of your masters.</div>
            </>
          )}
        </div>
        {connected && (
          <button className="btn danger" onClick={disconnect} disabled={busy}>Disconnect</button>
        )}
      </div>

      <div className="two-col" style={{ marginTop: 14 }}>
        {/* ───── Connection ───── */}
        <div className="card">
          <h3>Connection</h3>
          {loading ? (
            <div className="hint" style={{ marginTop: 14 }}>Loading…</div>
          ) : isProvisioning ? (
            <div className="hint" style={{ marginTop: 14 }}>
              Provisioning your bucket + CDN — connection details appear here once it's ready.
            </div>
          ) : connected ? (
            <>
              <div className="field"><label>Access key ID</label><input type="text" readOnly value={status?.accessKeyId ?? ""} /></div>
              <div className="field"><label>Region</label><input type="text" readOnly value={status?.region ?? ""} /></div>
              <div className="anno">ⓘ These keys created your bucket + CDN. To rotate keys, disconnect and reconnect.</div>
            </>
          ) : (
            <>
              <div className="field"><label>Access key ID</label><input type="text" value={keyId} onChange={(e) => setKeyId(e.target.value)} placeholder="AKIA…" /></div>
              <div className="field"><label>Secret access key</label><input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} /></div>
              <div className="field">
                <label>Region</label>
                <select value={region} onChange={(e) => setRegion(e.target.value)}>
                  {REGIONS.map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div className="anno">ⓘ We auto-create a private S3 bucket + CloudFront CDN in your account. Keys need s3:CreateBucket / PutObject / GetObject / ListBucket + cloudfront:CreateDistribution.</div>
              {(error || provisioning?.step === "error") && (
                <div className="hint" style={{ color: "var(--red)", marginTop: 8 }}>{error ?? provisioning?.error}</div>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
                <button className="btn primary" onClick={connect} disabled={busy || !keyId.trim() || !secret.trim()}>
                  {busy ? "Connecting…" : "Connect & provision"}
                </button>
              </div>
            </>
          )}
        </div>

        {/* ───── Usage ───── */}
        <div>
          <div className="card">
            <h3>Usage</h3>
            <div className="storage" style={{ border: "none", padding: "6px 0 0", margin: 0 }}>
              <div className="bar" style={{ marginTop: 10 }}><div className="fill" style={{ width: `${pct}%` }}></div></div>
              <div className="txt">
                {usage
                  ? `${gb(used)}${total > 0 ? ` of ${gb(total)}` : ""} used · ${usage.modelCount} models`
                  : "Loading usage…"}
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <Kv k="3D masters (.3dm / .stl / .glb)" v={gb(usage?.breakdown.masters ?? 0)} />
              <Kv k="Renders & turntables" v={gb(usage?.breakdown.renders ?? 0)} />
              <Kv k="Docs & certificates" v={gb(usage?.breakdown.docs ?? 0)} />
            </div>
          </div>
        </div>
      </div>

      <AgentsSection />
    </>
  );
}

/** ISO → "3 min ago" / "never" for an agent's last heartbeat. */
function relSeen(iso?: string): string {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = s / 60;
  if (m < 60) return `${Math.floor(m)} min ago`;
  const h = m / 60;
  if (h < 24) return `${Math.floor(h)} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

/**
 * On-prem render agents — the registry the Bulk Render on-premises path draws
 * from (BulkRenderPage points here). Register a machine (createAgent returns its
 * token ONCE), list machines with derived online/offline status + last heartbeat,
 * and remove one (deleteAgent).
 */
function AgentsSection() {
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [name, setName] = useState("");
  const [gpu, setGpu] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [newToken, setNewToken] = useState<{ name: string; token: string } | null>(null);

  const load = () => listAgents().then(setAgents).catch(() => setAgents([]));
  useEffect(() => { void load(); }, []);

  const register = async () => {
    if (!name.trim() || busy) return; // Enter key bypasses the disabled button — guard re-entry
    setBusy(true);
    setErr(null);
    try {
      const a = await createAgent(name.trim(), gpu.trim());
      setName("");
      setGpu("");
      if (a.agentToken) setNewToken({ name: a.name, token: a.agentToken });
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not register machine");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a: Agent) => {
    if (!confirm(`Remove ${a.name}? It will stop receiving render jobs.`)) return;
    try { await deleteAgent(a._id); await load(); }
    catch (e) { setErr(e instanceof Error ? e.message : "Could not remove machine"); }
  };

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <h3>On-prem render agents</h3>
      <div className="hint">Register your own machines to render on-premises from Bulk Render — files never leave your network. Install the Vault Agent on each, then paste its token below.</div>

      {agents === null ? (
        <div className="hint" style={{ marginTop: 12 }}>Loading machines…</div>
      ) : agents.length === 0 ? (
        <div className="hint" style={{ marginTop: 12 }}>No machines connected yet.</div>
      ) : (
        <table className="list-table on" style={{ marginTop: 12 }}>
          <thead><tr><th style={{ width: "40%" }}>Machine</th><th>GPU</th><th>Status</th><th>Last seen</th><th></th></tr></thead>
          <tbody>
            {agents.map((a) => (
              <tr key={a._id}>
                <td className="nm">{a.name}</td>
                <td>{a.gpu || "—"}</td>
                <td>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span className="conn-dot" style={a.status === "online" ? undefined : { background: "var(--muted)", boxShadow: "none" }}></span>
                    {a.status === "online" ? "Online" : "Offline"}
                  </span>
                </td>
                <td>{relSeen(a.lastSeenAt)}</td>
                <td style={{ textAlign: "right" }}>
                  <button className="btn ghost" style={{ padding: "5px 10px", fontSize: 12 }} onClick={() => remove(a)}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {newToken && (
        <div className="card" style={{ marginTop: 12, background: "var(--accent-soft)" }}>
          <b style={{ fontSize: 13 }}>{newToken.name} registered — copy its token now</b>
          <div className="hint" style={{ marginTop: 4 }}>Shown once. Paste it into the Vault Agent on that machine (VAULT_AGENT_TOKEN). Lose it and you'll need to remove the machine and register again.</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
            <span className="code" style={{ flex: 1, minWidth: 220, wordBreak: "break-all", padding: "8px 10px" }}>{newToken.token}</span>
            <CopyBtn className="btn ghost" label="Copy token" value={newToken.token} />
            <button className="btn ghost" style={{ padding: "6px 12px", fontSize: 12 }} onClick={() => setNewToken(null)}>Done</button>
          </div>
        </div>
      )}

      <div className="frow" style={{ marginTop: 12, alignItems: "flex-end" }}>
        <div className="field"><label>Machine name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="STUDIO-WORKSTATION-01" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void register(); } }} /></div>
        <div className="field"><label>GPU (optional)</label>
          <input type="text" value={gpu} onChange={(e) => setGpu(e.target.value)} placeholder="RTX 4090" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void register(); } }} /></div>
        <button className="btn primary" style={{ height: 38 }} onClick={register} disabled={busy || !name.trim()}>{busy ? "Registering…" : "Register machine"}</button>
      </div>
      {err && <div className="hint" style={{ color: "var(--red)", marginTop: 8 }}>{err}</div>}
    </div>
  );
}
